import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AdminSectionBaseComponent } from 'src/app/shared/admin-section-base';
import { ConfirmDeleteDialogComponent } from '../shared/confirm-delete-dialog/confirm-delete-dialog.component';
import { AdminApiService, Topic, TopicArea } from 'src/app/shared/services/admin-api.service';

@Component({
  standalone: false,
  selector: 'app-manage-topic-areas',
  templateUrl: './manage-topic-areas.component.html',
  styleUrls: ['./manage-topic-areas.component.scss'],
})
export class ManageTopicAreasComponent extends AdminSectionBaseComponent implements OnInit {
  topicAreas: TopicArea[] = [];
  topics: Topic[] = [];

  topicAreaForm: Partial<TopicArea> = {};
  private originalTopicAreaForm: Partial<TopicArea> = {};
  topicForm: Partial<Topic> = {};
  private originalTopicForm: Partial<Topic> = {};

  editingTopicAreaId: number | null = null;
  creatingTopicArea = false;
  editingTopicId: number | null = null;
  creatingTopic = false;

  constructor(
    private adminApiService: AdminApiService,
    private dialog: MatDialog,
    snackBar: MatSnackBar,
    cdr: ChangeDetectorRef,
  ) {
    super(snackBar, cdr);
    this.initPagination('topicAreas', 10);
    this.initPagination('topics', 10);
  }

  ngOnInit(): void {
    this.refreshAll();
  }

  refreshAll(): void {
    this.trackApiCall(this.adminApiService.listTopicAreas(this.token)).subscribe({
      next: (items) => { this.topicAreas = items; },
      error: (error) => this.handleError(error),
    });
    this.trackApiCall(this.adminApiService.listTopics(this.token)).subscribe({
      next: (items) => { this.topics = items; },
      error: (error) => this.handleError(error),
    });
  }

  openCreateTopicArea(): void {
    this.cancelTopicAreaEdit();
    this.creatingTopicArea = true;
  }

  openEditTopicArea(item: TopicArea): void {
    this.editTopicArea(item);
    this.creatingTopicArea = false;
    this.resetPage('topics');
  }

  openCreateTopicForCurrentTopicArea(): void {
    if (!this.editingTopicAreaId) {
      this.showError('Open a topic area in edit mode first.');
      return;
    }

    this.cancelTopicEdit();
    this.topicForm.topic_area = this.editingTopicAreaId;
    this.creatingTopic = true;
  }

  openEditTopic(item: Topic): void {
    this.editTopic(item);
    this.creatingTopic = false;
  }

  isEditingTopic(item: Topic): boolean {
    return this.editingTopicId === item.id;
  }

  isCreatingTopic(): boolean {
    return this.creatingTopic;
  }

  topicsForEditingTopicArea(): Topic[] {
    if (!this.editingTopicAreaId) {
      return [];
    }

    return this.topics.filter((item) => item.topic_area === this.editingTopicAreaId);
  }

  saveTopicArea(): void {
    if (!this.topicAreaForm.topic_area_title) {
      this.showError('topic_area_title is required.');
      return;
    }

    const { id: _ignoredId, ...topicAreaFormWithoutId } = this.topicAreaForm;

    const payload: Partial<TopicArea> = {
      ...topicAreaFormWithoutId,
      topic_area_title: (topicAreaFormWithoutId.topic_area_title || '').trim(),
      sequence: this.toNullableNumber(topicAreaFormWithoutId.sequence),
    };

    const request = this.editingTopicAreaId
      ? this.adminApiService.updateTopicArea(this.token, this.editingTopicAreaId, payload)
      : this.adminApiService.createTopicArea(this.token, payload);

    const actionKey = this.topicAreaSaveActionKey();

    this.trackActionCall(actionKey, request).subscribe({
      next: () => {
        this.showSuccess(`Topic area ${this.editingTopicAreaId ? 'updated' : 'created'} successfully.`);
        this.cancelTopicAreaEdit();
        this.trackActionCall(actionKey, this.adminApiService.listTopicAreas(this.token)).subscribe((items) => (this.topicAreas = items));
      },
      error: (error) => this.handleError(error),
    });
  }

  editTopicArea(item: TopicArea): void {
    this.editingTopicAreaId = item.id;
    this.topicAreaForm = { ...item };
    this.originalTopicAreaForm = { ...item };
    this.cancelTopicEdit();
  }

  cancelTopicAreaEdit(): void {
    this.cancelTopicEdit();
    this.creatingTopicArea = false;
    this.editingTopicAreaId = null;
    this.topicAreaForm = {};
    this.originalTopicAreaForm = {};
  }

  get isTopicAreaEditorVisible(): boolean {
    return this.creatingTopicArea || Boolean(this.editingTopicAreaId);
  }

  get isTopicAreaFormDirty(): boolean {
    if (!this.editingTopicAreaId) { return false; }
    return (this.topicAreaForm.topic_area_title || '').trim() !== (this.originalTopicAreaForm.topic_area_title || '').trim()
      || this.toNullableNumber(this.topicAreaForm.sequence) !== this.toNullableNumber(this.originalTopicAreaForm.sequence);
  }

  deleteTopicArea(item: TopicArea): void {
    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Delete Topic Area',
        message: 'Are you sure you want to permanently delete this topic area?',
        itemName: item.topic_area_title || `Topic Area ${item.id}`
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const actionKey = this.topicAreaDeleteActionKey(item.id);
        this.trackActionCall(actionKey, this.adminApiService.deleteTopicArea(this.token, item.id)).subscribe({
          next: () => {
            this.showSuccess('Topic area deleted successfully.');
            if (this.editingTopicAreaId === item.id) {
              this.cancelTopicAreaEdit();
            }
            this.trackActionCall(actionKey, this.adminApiService.listTopicAreas(this.token)).subscribe((items) => (this.topicAreas = items));
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  saveTopic(): void {
    if (!this.editingTopicAreaId) {
      this.showError('Open a topic area in edit mode first.');
      return;
    }

    if (!this.topicForm.topic_title) {
      this.showError('topic_title is required.');
      return;
    }

    const parentTopicAreaId = this.editingTopicAreaId;
    const { id: _ignoredId, topic_area: _ignoredTopicArea, ...topicFormWithoutIds } = this.topicForm;

    const payload: Partial<Topic> = {
      ...topicFormWithoutIds,
      topic_title: (topicFormWithoutIds.topic_title || '').trim(),
      sequence: this.toNullableNumber(topicFormWithoutIds.sequence),
      topic_area: parentTopicAreaId,
    };

    const request = this.editingTopicId
      ? this.adminApiService.updateTopic(this.token, this.editingTopicId, payload)
      : this.adminApiService.createTopic(this.token, payload);

    const actionKey = this.topicSaveActionKey();

    this.trackActionCall(actionKey, request).subscribe({
      next: () => {
        this.showSuccess(`Topic ${this.editingTopicId ? 'updated' : 'created'} successfully.`);
        this.cancelTopicEdit();
        this.trackActionCall(actionKey, this.adminApiService.listTopics(this.token)).subscribe((items) => (this.topics = items));
      },
      error: (error) => this.handleError(error),
    });
  }

  editTopic(item: Topic): void {
    this.editingTopicId = item.id;
    this.originalTopicForm = { ...item };
    this.topicForm = { ...item, topic_area: this.editingTopicAreaId || item.topic_area };
  }

  cancelTopicEdit(): void {
    this.editingTopicId = null;
    this.creatingTopic = false;
    this.topicForm = { topic_area: this.editingTopicAreaId || undefined };
    this.originalTopicForm = {};
  }

  get isTopicFormDirty(): boolean {
    if (!this.editingTopicId) { return false; }
    return (this.topicForm.topic_title || '').trim() !== (this.originalTopicForm.topic_title || '').trim()
      || this.toNullableNumber(this.topicForm.sequence) !== this.toNullableNumber(this.originalTopicForm.sequence);
  }

  deleteTopic(item: Topic): void {
    if (!this.editingTopicAreaId) {
      this.showError('Open a topic area in edit mode first.');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '400px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'app-dialog-panel',
      data: {
        title: 'Delete Topic',
        message: 'Are you sure you want to permanently delete this topic?',
        itemName: item.topic_title || `Topic ${item.id}`
      }
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        const actionKey = this.topicDeleteActionKey(item.id);
        this.trackActionCall(actionKey, this.adminApiService.deleteTopic(this.token, item.id)).subscribe({
          next: () => {
            this.showSuccess('Topic deleted successfully.');
            if (this.isEditingTopic(item)) {
              this.cancelTopicEdit();
            }
            this.trackActionCall(actionKey, this.adminApiService.listTopics(this.token)).subscribe((items) => (this.topics = items));
          },
          error: (error) => this.handleError(error),
        });
      }
    });
  }

  refreshActionKey(): string {
    return 'refresh-topic-areas';
  }

  topicAreaSaveActionKey(): string {
    return 'topic-area-save';
  }

  topicAreaDeleteActionKey(topicAreaId: number): string {
    return `topic-area-delete:${topicAreaId}`;
  }

  topicSaveActionKey(): string {
    return 'topic-save';
  }

  topicDeleteActionKey(topicId: number): string {
    return `topic-delete:${topicId}`;
  }
}
